import ee from '../../services/earth-engine';
import { eeEval } from '../utils/index.js';
import { trainingCollection } from './depthImage';

/**
 * @constant SAMPLE_SLICE_SIZE
 * @description How many soundings one Earth Engine request samples.
 * Sampled in one request, the 175 908 points of the 50 m grid of the Babitonga bay passed the five
 * minutes Earth Engine allows an interactive request; the 4 872 points of the 300 m grid over the
 * same composite took 65 s. Slices of this size stay well under the limit.
 * @memberof module:Bathymetry
 */
export const SAMPLE_SLICE_SIZE = 20000;

/**
 * @constant SAMPLE_CONCURRENCY
 * @description How many slices are sampled at once. Concurrent requests share the user's Earth
 * Engine memory quota, so a few at a time rather than all of them.
 * @memberof module:Bathymetry
 */
const SAMPLE_CONCURRENCY = 4;

/**
 * @function roundSample
 * @description A sampled value with six decimals, which is far below the noise of a reflectance or
 * a sounding and keeps the values sent back to Earth Engine for the fit about half as long.
 * @param {Number} value
 * @memberof module:Bathymetry
 * @returns {Number}
 */
const roundSample = (value) => (value == null ? null : Math.round(value * 1e6) / 1e6);

/**
 * @function runInSlices
 * @description Runs one Earth Engine request per slice of the survey, {@link SAMPLE_CONCURRENCY}
 * at a time, and hands back each slice's answer in the survey's order.
 * The survey is cut in file order, which for a gridded survey runs in bands across the area, so
 * each request touches only part of it.
 * @param {Array<Array<Number>>} matrix - The soundings, as `[x, y, depth]` rows.
 * @param {Function} task - Called with a slice of rows, resolving to that slice's answer.
 * @param {Function} [onProgress] - Called with `{done, total}` before the first slice and after each.
 * **Called by:
 * @see {sampleTrainingPoints}
 * @see {computeDepthMetrics}
 * @memberof module:Bathymetry
 * @returns {Promise<Array>} The answers, one per slice.
 */
export const runInSlices = async (matrix, task, onProgress) => {
  const slices = [];
  for (let start = 0; start < matrix.length; start += SAMPLE_SLICE_SIZE) {
    slices.push(matrix.slice(start, start + SAMPLE_SLICE_SIZE));
  }
  const answers = new Array(slices.length);
  let next = 0;
  let done = 0;
  if (onProgress) onProgress({ done, total: slices.length });
  const runSlices = async () => {
    while (next < slices.length) {
      const index = next;
      next += 1;
      answers[index] = await task(slices[index]);
      done += 1;
      if (onProgress) onProgress({ done, total: slices.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(SAMPLE_CONCURRENCY, slices.length) }, runSlices));
  return answers;
};

/**
 * @function sampleTrainingPoints
 * @description Samples an image at the soundings and brings the values back to the browser, a
 * slice of the survey per request - see {@link runInSlices}. A band that comes back masked at a
 * point stays `null` in its row, as it was absent from the sampled feature, and is left to each
 * chart and fit. Without the raw rows - a session saved before they were kept - the whole
 * collection is sampled in one request, as before.
 * @param {Object} params
 * @param {Object} params.image - The `ee.Image` holding the bands to sample.
 * @param {Array<Array<Number>>} [params.matrix] - The soundings, as `[x, y, depth]` rows.
 * @param {Object} params.collection - The same soundings as an `ee.FeatureCollection`, used when
 *  `matrix` is missing.
 * @param {String} [params.crs] - The CRS of the soundings' `x` and `y`.
 * @param {Number} params.scale - Sampling scale, in metres.
 * @param {String[]} params.bands - The bands to read.
 * @param {Function} [params.onProgress] - Called with `{done, total}` after each slice.
 * @memberof module:Bathymetry
 * @returns {Promise<Array<Array<Number>>>} `[or_depth, ...bands]` rows.
 */
export const sampleTrainingPoints = async ({ image, matrix, collection, crs, scale, bands, onProgress }) => {
  const fields = ['or_depth', ...bands];
  const sample = async (points) => {
    const sampled = image.sampleRegions({
      collection: points,
      properties: ['or_depth'],
      scale,
      geometries: false,
      tileScale: 4,
    });
    const asRows = sampled.map((feature) => ee.Feature(null, { row: ee.List(fields.map((key) => feature.get(key))) }));
    const result = await eeEval(asRows.aggregate_array('row'));
    return (result || []).map((row) => row.map(roundSample));
  };
  if (!matrix?.length) return sample(collection);
  const answers = await runInSlices(matrix, (slice) => sample(trainingCollection(slice, crs)), onProgress);
  return answers.flat();
};

/**
 * @function samplesCollection
 * @description Sampled values back as an `ee.FeatureCollection`, for the model fits on the result
 * page. The values travel as one JSON string decoded on the server - as a detailed area does in
 * {@link aoiGeometry} - so the fit reads the samples already taken instead of sampling the
 * composite a second time at every sounding.
 * @param {Array<Array<Number>>} rows - `[or_depth, ...bands]` rows, from {@link sampleTrainingPoints}.
 * @param {String[]} bands - The bands after `or_depth`, in the rows' order.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection` of features without geometry, carrying `or_depth`
 *  and the bands.
 */
export const samplesCollection = (rows, bands) => {
  const fields = ee.List(['or_depth', ...bands]);
  return ee.FeatureCollection(
    ee
      .List(ee.String(JSON.stringify(rows)).decodeJSON())
      .map((row) => ee.Feature(null, ee.Dictionary.fromLists(fields, ee.List(row)))),
  );
};

/**
 * @constant MAX_PLOTTED_POINTS
 * @description How many soundings a map or a chart draws at most. Each one is a Google Maps circle
 * or a chart point, and the points of a survey in UTM come back through an Earth Engine `evaluate`,
 * which refuses a collection of more than 5000 elements - a 175 908-point grid neither drew nor
 * converted. Larger surveys are drawn thinned; the model is still fitted on every point, and the
 * charts' trend line and extinction depth are computed on every point too.
 * @memberof module:Bathymetry
 */
export const MAX_PLOTTED_POINTS = 5000;

/**
 * @function plottedSample
 * @description Evenly spaced items of a survey, at most {@link MAX_PLOTTED_POINTS} of them, kept
 * in file order so a gridded survey thins into a coarser grid rather than into clusters.
 * @param {Array} items - Soundings as `[x, y, depth]` rows, or chart points.
 * **Called by:
 * @see {showPlotsInMap}
 * @see {reprocessCharts}
 * @memberof module:Bathymetry
 * @returns {Array}
 */
export const plottedSample = (items) => {
  if (items.length <= MAX_PLOTTED_POINTS) return items;
  const step = items.length / MAX_PLOTTED_POINTS;
  return Array.from({ length: MAX_PLOTTED_POINTS }, (_, i) => items[Math.floor(i * step)]);
};

/**
 * @function scatterOf
 * @description One predictor against depth, read from rows already in the browser.
 * @param {Array<Array<Number>>} rows - `[or_depth, ...bands]` rows.
 * @param {String[]} bands - The bands after `or_depth`, in the rows' order.
 * @param {String} band - The predictor to plot.
 * @memberof module:Bathymetry
 * @returns {Array<{x: Number, y: Number}>}
 */
export const scatterOf = (rows, bands, band) => {
  const column = bands.indexOf(band) + 1;
  if (column === 0) return [];
  return rows
    .filter((row) => row[column] != null && row[0] != null)
    .map((row) => ({ x: Number(row[column]), y: Number(row[0]) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
};
